import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";

const VideoJobsContext = createContext(null);
const isActive = (j) => j.status === "queued" || j.status === "running";

export const VideoJobsProvider = ({ children }) => {
  const [jobs, setJobs] = useState(null);
  const [unseen, setUnseen] = useState(0);
  const prev = useRef({});
  const timer = useRef(null);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const pathRef = useRef(pathname);
  pathRef.current = pathname;

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/videos");
      data.forEach((j) => {
        const was = prev.current[j.id];
        if (was && isActive({ status: was }) && !isActive(j)) {
          const short = j.prompt.length > 60 ? j.prompt.slice(0, 60) + "…" : j.prompt;
          if (j.status === "done") {
            toast.success(`Video ready: ${short}`, { duration: 8000, action: { label: "Watch", onClick: () => navigate("/studio") } });
          } else {
            toast.error(`Video failed: ${short}`, { description: j.error, duration: 8000, action: { label: "Open studio", onClick: () => navigate("/studio") } });
          }
          if (pathRef.current !== "/studio") setUnseen((n) => n + 1);
        }
      });
      prev.current = Object.fromEntries(data.map((j) => [j.id, j.status]));
      setJobs(data);
      return data;
    } catch (_) {
      return [];
    }
  }, [navigate]);

  const startPolling = useCallback(() => {
    if (timer.current) return;
    timer.current = setInterval(async () => {
      const data = await refresh();
      if (!data.some(isActive)) { clearInterval(timer.current); timer.current = null; }
    }, 6000);
  }, [refresh]);

  useEffect(() => {
    refresh().then((data) => { if (data.some(isActive)) startPolling(); });
    return () => timer.current && clearInterval(timer.current);
  }, [refresh, startPolling]);

  useEffect(() => { if (pathname === "/studio") setUnseen(0); }, [pathname]);

  const addJob = useCallback((job) => {
    prev.current[job.id] = job.status;
    setJobs((j) => [job, ...(j || [])]);
    startPolling();
  }, [startPolling]);

  const updateJob = useCallback((id, patch) => setJobs((j) => j.map((x) => (x.id === id ? { ...x, ...patch } : x))), []);
  const removeJob = useCallback((id) => { delete prev.current[id]; setJobs((j) => j.filter((x) => x.id !== id)); }, []);

  return <VideoJobsContext.Provider value={{ jobs, unseen, addJob, updateJob, removeJob, refresh }}>{children}</VideoJobsContext.Provider>;
};

export const useVideoJobs = () => useContext(VideoJobsContext);
